import { trpc } from '@/main'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { reportSearchDeps, reportQueryOptions, PANES_WITH_OWN_QUERY } from '@/hooks/use-report'
import { noop } from '@tanstack/react-query'

/**
 * Layout for a selected functional group (`/orgs/$orgId/groups/$groupId/*`). It renders real content, so per
 * AGENTS.md it OWNS the orgId/groupId-keyed chrome its subtree reads — `getGroups`, `getOrgGroupUsers` (read by
 * the users panel in orgs.tsx), and the report data shared by all three tab children.
 *
 * The board/distribution leaves keep only `getOrgGroupProfile`; the report leaf has no loader.
 */
export const Route = createFileRoute('/_app/orgs/$orgId/groups/$groupId')({
  loaderDeps: ({ search }) => reportSearchDeps(search),
  context: ({ deps, params }) => ({
    reportQueryOptions: reportQueryOptions(deps, params),
    // The board/distribution panes' profile, owned HERE so an ámbito switch re-keys THIS match rather than
    // a leaf's: a leaf at `pendingMs: 0` paints its skeleton the moment its match reloads, and this layout has
    // no pendingComponent at all. Only the PREFETCH is gated on the pane below (see the loader) — the key
    // still lives here, so both readers get the one object.
    orgGroupProfileQueryOptions: trpc.orgs.getOrgGroupProfile.queryOptions({
      orgId: params.orgId,
      groupId: params.groupId,
      orgType: deps.orgType,
    }),
    // Both are read ABOVE this route (the groups breadcrumb + group name in orgs.tsx, the users panel it
    // renders), but only this route has the ids — so it owns the prefetch and everyone shares the factory.
    orgGroupsQueryOptions: trpc.orgs.getGroups.queryOptions(params.orgId),
    orgGroupUsersQueryOptions: trpc.orgs.getOrgGroupUsers.queryOptions({
      orgId: params.orgId,
      groupId: params.groupId,
      orgType: deps.orgType,
    }),
  }),
  loader: ({ context, location }) => {
    context.queryClient.query(context.orgGroupsQueryOptions).catch(noop)
    context.queryClient.query(context.orgGroupUsersQueryOptions).catch(noop)
    if (context.reportQueryOptions) context.queryClient.query(context.reportQueryOptions).catch(noop)
    // Board and distribution are the ONLY readers, and this layout also sits under /report and the index —
    // where warming it is a request per visit that nothing consumes.
    if (PANES_WITH_OWN_QUERY.test(location.pathname)) {
      context.queryClient.query(context.orgGroupProfileQueryOptions).catch(noop)
    }
  },
  component: RouteComponent,
  // No pendingComponent: this passthrough layout just renders <AnimatedOutlet/>. The data-cold case is
  // covered by the parent orgs.tsx (<Suspense fallback={ReportPanelSkeleton}>), and each tab leaf
  // (report/board/distribution) owns its own tab-specific skeleton — a generic one here would mismatch.
})

function RouteComponent() {
  return <Outlet />
}
